import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faClock } from '@fortawesome/free-solid-svg-icons';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';

import noCourseImg from '@src/assets/images/no-course-image.svg';
import { getFullImageUrl, getStartDateDisplay } from '@src/generic/course-card/utils';
import type { CourseCardProps } from '@src/generic/course-card/types';
import messages from './messages';

interface MarketingCourseCardProps extends CourseCardProps {
  courseOverview?: string;
  courseLanguage?: string;
}

const MarketingCourseCard = ({
  isLoading,
  courseId,
  courseOrg,
  courseName,
  courseImageUrl,
  courseStartDate,
  courseAdvertisedStart,
  courseLanguage,
}: MarketingCourseCardProps) => {
  const intl = useIntl();
  const config = getConfig();
  const [resolvedImageSrc, setResolvedImageSrc] = useState(getFullImageUrl(courseImageUrl) || noCourseImg);

  useEffect(() => {
    setResolvedImageSrc(getFullImageUrl(courseImageUrl) || noCourseImg);
  }, [courseImageUrl]);

  const startDateDisplay = (courseStartDate || courseAdvertisedStart)
    ? getStartDateDisplay({
      start: courseStartDate,
      advertisedStart: courseAdvertisedStart,
    }, intl)
    : null;

  const languageLabel = (courseLanguage || intl.locale || 'fr').toUpperCase();
  const siteLabel = config.SITE_NAME?.replace(/\s+Dev$/i, '').trim() || 'WutiSkill';
  const cardClassName = `catalog-marketing-course-card${isLoading ? ' catalog-marketing-course-card--loading' : ''}`;

  const content = (
    <>
      <div className="catalog-marketing-course-card__media">
        {isLoading ? (
          <div className="catalog-marketing-course-card__media-skeleton" />
        ) : (
          <img
            src={resolvedImageSrc}
            alt={intl.formatMessage(messages.imageAlt, {
              courseName: courseName || siteLabel,
            })}
            className="catalog-marketing-course-card__image"
            onError={() => {
              if (resolvedImageSrc !== noCourseImg) {
                setResolvedImageSrc(noCourseImg);
              }
            }}
          />
        )}
      </div>

      <div className="catalog-marketing-course-card__body">
        <div className="catalog-marketing-course-card__meta-row">
          <span className="catalog-marketing-course-card__eyebrow">{courseOrg || siteLabel}</span>
          {startDateDisplay ? (
            <span className="catalog-marketing-course-card__time">
              <FontAwesomeIcon icon={faClock} />
              <span>{startDateDisplay}</span>
            </span>
          ) : null}
        </div>

        <h3 className="catalog-marketing-course-card__title">
          {courseName || intl.formatMessage(messages.defaultTitle)}
        </h3>

        <div className="catalog-marketing-course-card__footer">
          <div className="catalog-marketing-course-card__tags">
            <span>{languageLabel}</span>
          </div>
          <span className="catalog-marketing-course-card__cta">
            {intl.formatMessage(messages.viewCourse)}
            <FontAwesomeIcon icon={faArrowRight} />
          </span>
        </div>
      </div>
    </>
  );

  if (isLoading || !courseId) {
    return <article className={cardClassName}>{content}</article>;
  }

  return (
    <Link className={cardClassName} to={`/courses/${courseId}/about`}>
      {content}
    </Link>
  );
};

MarketingCourseCard.defaultProps = {
  isLoading: false,
  courseId: undefined,
  courseOrg: undefined,
  courseName: undefined,
  courseImageUrl: undefined,
  courseStartDate: undefined,
  courseAdvertisedStart: undefined,
  courseLanguage: undefined,
};

export default MarketingCourseCard;
